import type { LocationQueryValue } from 'vue-router'

/**
 * Coalesce a possibly-repeated query value down to a single string.
 *
 * `vue-router` types every query parameter as `string | null | (string | null)[]`
 * because a URL may repeat or flag-set it (`?id=a&id=b`, `?id`). Readers that expect
 * one value take the first string and treat everything else as absent.
 */
export function firstQueryValue(
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string | undefined {
  const single = Array.isArray(value) ? value[0] : value
  return typeof single === 'string' ? single : undefined
}

/**
 * Read a repeatable query parameter as the list it stands for.
 *
 * The mirror of {@link firstQueryValue} for readers that expect a set. `vue-router`
 * hands one occurrence over as a bare `string` and several as `string[]`, so anything
 * reading `route.query[key] as string[]` silently gets a string the moment the set
 * has one member — and `[...'uuid']` is 36 one-character ids. Both shapes are
 * normalised here; a flag-set (`?ids`) or a missing parameter is the empty list, which
 * is also how the generated API client spells "no restriction".
 */
export function queryValues(
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string[] {
  const many = Array.isArray(value) ? value : [value]
  return many.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
}
